import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import NavigationBar from "components/Navigation";
import { reservationService, MyReservation } from 'services/reservationService';
import {
  Body,
  Header,
  BackButton,
  ReturnContainer,
  ImageContainer,
  Container1,
  Container2,
  CheckContainer,
  ReturnButton
} from './Return.styles';
import ReturnModal from 'components/Modal/ReturnModal';

interface CheckList {
  checkList1: boolean;
  checkList2: boolean;
  checkList3: boolean;
  checkList4: boolean;
}

const Return: React.FC = () => {
  const { reservationId } = useParams<{ reservationId: string }>();
  const navigate = useNavigate();
  const [reservation, setReservation] = useState<MyReservation | null>(null);
  const [images, setImages] = useState<File[]>([]);
  const [photos, setPhotos] = useState<string[]>([]);
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [returnedReservation, setReturnedReservation] = useState<MyReservation | null>(null);
  const [checkList, setCheckList] = useState<CheckList>({
    checkList1: false,
    checkList2: false,
    checkList3: false,
    checkList4: false,
  });

  useEffect(() => {
    const fetchReservation = async () => {
      if (!reservationId) {
        setError('예약 ID가 없습니다.');
        setIsLoading(false);
        return;
      }

      try {
        const data = await reservationService.getReservationById(parseInt(reservationId));
        setReservation(data);
      } catch (error) {
        console.error('Failed to fetch reservation:', error);
        setError('예약 정보를 불러오는데 실패했습니다.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchReservation();
  }, [reservationId]);

  const handleGoBack = () => {
    navigate(-1);
  };

  const onChangeImg = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;

    if (selectedFiles && selectedFiles.length > 0) {
      const newFiles = Array.from(selectedFiles).slice(0, 4 - images.length);

      if (images.length + newFiles.length > 4) {
        alert('이미지는 최대 4개까지만 업로드 가능합니다.');
        return;
      }

      const newPhotos = newFiles.map((file) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        return new Promise<string>((resolve) => {
          reader.onload = () => resolve(reader.result as string);
        });
      });

      Promise.all(newPhotos).then((results) => {
        setPhotos([...photos, ...results]);
        setImages([...images, ...newFiles]);
      });
    }
  };

  const handleReturn = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!checkList.checkList1 || !checkList.checkList2 || !checkList.checkList3 || !checkList.checkList4) {
      alert('모든 체크리스트를 확인해주세요.');
      return;
    }

    if (images.length === 0) {
      alert('반납 사진을 1장 이상 등록해주세요.');
      return;
    }

    if (!reservationId) {
      alert('예약 ID가 없습니다.');
      return;
    }

    try {
      // 1. 이미지 업로드
      const imageUrls = await reservationService.uploadReservationImages(images);

      // 2. 반납 요청
      const returnedData = await reservationService.returnReservationById(parseInt(reservationId), imageUrls);

      // 3. 반납된 예약 정보 저장
      setReturnedReservation(returnedData);
      setIsOpenModal(true);
    } catch (error) {
      console.error('Failed to return reservation:', error);
      alert('반납 처리에 실패했습니다.');
    }
  };

  const openCamera = () => {
    document.getElementById('photoFile')?.click();
  };

  const handleCheckListToggle = (listName: keyof CheckList) => {
    setCheckList((prevCheckList) => ({
      ...prevCheckList,
      [listName]: !prevCheckList[listName],
    }));
  };

  const formatDate = (isoString: string): string => {
    const date = new Date(isoString);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatTime = (isoString: string): string => {
    const date = new Date(isoString);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const calculateEndTime = (startTime: string, durationMinutes: number): string => {
    const start = new Date(startTime);
    const end = new Date(start.getTime() + durationMinutes * 60 * 1000);
    const hours = end.getHours().toString().padStart(2, '0');
    const minutes = end.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  if (isLoading) {
    return (
      <div className="ReturnBody">
        <Body>
          <Header>
            <BackButton onClick={handleGoBack}>
              <FontAwesomeIcon icon={faChevronLeft} />
            </BackButton>
            반납하기
          </Header>
          <div style={{ padding: '20px', textAlign: 'center' }}>로딩 중...</div>
        </Body>
        <NavigationBar />
      </div>
    );
  }

  if (error || !reservation) {
    return (
      <div className="ReturnBody">
        <Body>
          <Header>
            <BackButton onClick={handleGoBack}>
              <FontAwesomeIcon icon={faChevronLeft} />
            </BackButton>
            반납하기
          </Header>
          <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
            {error || '예약을 찾을 수 없습니다.'}
          </div>
        </Body>
        <NavigationBar />
      </div>
    );
  }

  return (
    <div className="ReturnBody">
      <Body>
        <Header>
          <BackButton onClick={handleGoBack}>
            <FontAwesomeIcon icon={faChevronLeft} />
          </BackButton>
          반납하기
        </Header>
        <ReturnContainer>
          <Container1>
            <ImageContainer>
              {reservation.facility.imageUrl ? (
                <img
                  src={reservation.facility.imageUrl}
                  alt={reservation.facility.name}
                  style={{ width: '100px', height: '100px', borderRadius: '12px', objectFit: 'cover' }}
                />
              ) : (
                <div style={{ width: '100px', height: '100px', borderRadius: '12px', backgroundColor: '#E5E5E5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '40px' }}>
                  🏢
                </div>
              )}
            </ImageContainer>
            <div className="Info">
              <div className="InfoTitle">{reservation.facility.name}</div>
              <div style={{ fontSize: '14px', color: '#666', textAlign: 'left' }}>
                <div>이용일 {formatDate(reservation.startTime)}</div>
                <div style={{ marginTop: '6px' }}>이용시간 {formatTime(reservation.startTime)}~{calculateEndTime(reservation.startTime, reservation.durationMinutes)}</div>
              </div>
            </div>
          </Container1>
          <div style={{ marginTop: '20px', marginLeft: '16px', marginBottom: '10px', color: '#999', fontSize: '14px', textAlign: 'left' }}>
            반납 사진 등록하기
          </div>
          <Container2>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
              {[0, 1, 2, 3].map((index) => (
                <div
                  key={index}
                  style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '8px',
                    backgroundColor: '#E5E5E5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                  }}
                >
                  {photos[index] ? (
                    <img
                      src={photos[index]}
                      alt={`Selected ${index}`}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : null}
                </div>
              ))}
            </div>
            <input
              type="file"
              name="photoFile"
              id="photoFile"
              accept="image/*"
              capture="environment"
              onChange={onChangeImg}
              style={{ display: 'none' }}
              multiple
            />
            <button className="cab" onClick={openCamera}>
              📸 반납 사진 촬영
            </button>
          </Container2>
        </ReturnContainer>
        <CheckContainer>
          <div className="title">
            <div className="Info1">잠깐, <span style={{ color: '#FFA500' }}>반납하기 전 확인</span>하셨나요?</div>
            <div className="Info2">체크리스트를 작성하여 반납 전 다시 한 번 체크해보아요.</div>
          </div>
          <div className="check1">
            <input
              className="box1"
              type="checkbox"
              checked={checkList.checkList1}
              onChange={() => handleCheckListToggle('checkList1')}
            />
            <div>소등은 하였나요?</div>
          </div>
          <div className="check2">
            <input
              className="box1"
              type="checkbox"
              checked={checkList.checkList2}
              onChange={() => handleCheckListToggle('checkList2')}
            />
            <div>에어컨/히터는 종료하였나요?</div>
          </div>
          <div className="check3">
            <input
              className="box1"
              type="checkbox"
              checked={checkList.checkList3}
              onChange={() => handleCheckListToggle('checkList3')}
            />
            <div>창문은 잠궜나요?</div>
          </div>
          <div className="check4">
            <input
              className="box1"
              type="checkbox"
              checked={checkList.checkList4}
              onChange={() => handleCheckListToggle('checkList4')}
            />
            <div>쓰레기는 버렸나요?</div>
          </div>
        </CheckContainer>
        <ReturnButton onClick={handleReturn}>반납하기</ReturnButton>
        {returnedReservation && (
          <ReturnModal
            isOpen={isOpenModal}
            onClose={() => setIsOpenModal(false)}
            reservation={returnedReservation}
          />
        )}
      </Body>
      <NavigationBar />
    </div>
  );
};

export default Return;
